import React, { useContext, useState } from 'react';
import {
  Box,
  Text,
  VStack,
  Flex,
  IconButton,
  Button,
  Link,
  List,
  ListItem,
  ListIcon,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { AttachmentIcon } from '@chakra-ui/icons';
import { FaEye, FaTrash, FaUpload } from 'react-icons/fa';
import { useDropzone } from 'react-dropzone';
import { uploadMediaKit } from '../../../services/firebaseService';
import { influencerApi } from 'services/api/influencer';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { ff } from '../../../theme';

interface MediaKitEditProps {
  mediaKit: string;
  setMediaKit: (url: string) => void;
  lastSavedMediaKit: string | null;
  onMediaKitSaved: (url: string) => void;
  onBack: () => void;
}

const MediaKitEdit: React.FC<MediaKitEditProps> = ({
  mediaKit,
  setMediaKit,
  lastSavedMediaKit,
  onMediaKitSaved,
  onBack,
}) => {
  const toast = useToast();
  const { influencerData } = useContext(InfluencerDataContext);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  /**
   * Autosave media kit URL
   */
  const autosaveMediaKit = async (mediaKitUrl: string) => {
    if (!influencerData?.id || mediaKitUrl === lastSavedMediaKit) return;
    
    try {
      setIsSaving(true);
      
      await influencerApi.updateProfile({
        mediaKit: mediaKitUrl
      }, influencerData.id);
      
      // Update last saved value on success
      onMediaKitSaved(mediaKitUrl);
    } catch (error) {
      console.error('Error autosaving media kit:', error);
      // Revert to last saved value
      setMediaKit(lastSavedMediaKit || '');
      toast({
        title: "Couldn't save media kit",
        description: "Reverted to last saved value",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onDrop = async (acceptedFiles: File[]) => {
    try {
      setIsUploading(true);
      const file = acceptedFiles[0];
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const mediaKitURL = await uploadMediaKit(uint8Array, file.type, file.name);
      setMediaKit(mediaKitURL);
      
      // Autosave after upload
      await autosaveMediaKit(mediaKitURL);
    } catch (error) {
      console.error('Error uploading media kit:', error);
      toast({
        title: "Upload failed",
        description: "Failed to upload media kit",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleDeleteMediaKit = async () => {
    setMediaKit('');
    await autosaveMediaKit('');
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    maxFiles: 1,
    maxSize: 10485760, // 10MB
  });

  return (
    <Box>
      {/* Description */}
      <Box bg={ff.grey} borderRadius="20px" p={4} mb={6}>
        <Flex justify="space-between" align="center">
          <Text color={ff.text2} fontSize="14px">
            Upload your media kit to share with brands. Accepted formats: PDF, PNG, JPG (max 10MB)
          </Text>
          {(isSaving || isUploading) && (
            <Flex align="center" ml={3} flexShrink={0}>
              <Spinner size="sm" color={ff.muted} mr={2} />
              <Text fontSize="12px" color={ff.text2}>{isUploading ? 'Uploading...' : 'Saving...'}</Text>
            </Flex>
          )}
        </Flex>
      </Box>

      <VStack spacing={6} align="stretch" mb={6}>
        {/* Upload Area */}
        <Box
          {...getRootProps()}
          border="1.5px dashed"
          borderColor={isDragActive ? ff.ink : ff.border}
          borderRadius="20px"
          p={8}
          textAlign="center"
          bg={isDragActive ? ff.limeSurface : "white"}
          cursor={(isSaving || isUploading) ? "not-allowed" : "pointer"}
          opacity={(isSaving || isUploading) ? 0.6 : 1}
          transition="all 0.2s"
          _hover={{
            borderColor: ff.ink,
            bg: ff.grey,
          }}
          pointerEvents={(isSaving || isUploading) ? "none" : "auto"}
        >
          <input {...getInputProps()} />
          <VStack spacing={4}>
            <Flex
              w="60px"
              h="60px"
              align="center"
              justify="center"
              bg={ff.lime}
              borderRadius="full"
            >
              <FaUpload size={22} color={ff.ink} />
            </Flex>
            <Box>
              <Text textStyle="display" fontSize="20px" color={ff.ink} mb={1}>
                Upload media kit
              </Text>
              <Text fontSize="14px" color={ff.text2}>
                Drag and drop your file here, or click to browse
              </Text>
            </Box>
            <Button variant="ink" h="44px" px={6}>
              Choose file
            </Button>
          </VStack>
        </Box>

        {/* Current Media Kit */}
        {mediaKit && (
          <Box
            border="1px solid"
            borderColor={ff.line}
            borderRadius="20px"
            p={4}
            bg="white"
          >
            <Flex align="center" justify="space-between">
              <Flex align="center" flex={1}>
                <Box mr={3}>
                  <AttachmentIcon boxSize={5} color={ff.ink} />
                </Box>
                <Box flex={1}>
                  <Text fontWeight={600} fontSize="14px" color={ff.ink}>
                    Current media kit
                  </Text>
                  <Text fontSize="12px" color={ff.text2}>
                    Click to view or replace
                  </Text>
                </Box>
              </Flex>
              <Flex gap={2}>
                <Link href={mediaKit} isExternal>
                  <IconButton
                    icon={<FaEye />}
                    w="44px"
                    h="44px"
                    borderRadius="full"
                    aria-label="View Media Kit"
                    variant="ghost"
                    color={ff.ink}
                  />
                </Link>
                <IconButton
                  icon={isSaving ? <Spinner size="xs" /> : <FaTrash />}
                  w="44px"
                  h="44px"
                  borderRadius="full"
                  aria-label="Delete Media Kit"
                  variant="ghost"
                  color={ff.danger}
                  onClick={handleDeleteMediaKit}
                  isDisabled={isSaving}
                />
              </Flex>
            </Flex>
          </Box>
        )}

        {/* What to Include */}
        <Box>
          <Text textStyle="eyebrow" color={ff.text2} mb={3}>
            What to include
          </Text>
          <List spacing={3}>
            <ListItem display="flex" alignItems="flex-start">
              <ListIcon as={() => <Box w="6px" h="6px" borderRadius="full" bg={ff.ink} mt={2} mr={3} />} />
              <Text fontSize="14px" color={ff.body}>
                Your social media statistics and demographics
              </Text>
            </ListItem>
            <ListItem display="flex" alignItems="flex-start">
              <ListIcon as={() => <Box w="6px" h="6px" borderRadius="full" bg={ff.ink} mt={2} mr={3} />} />
              <Text fontSize="14px" color={ff.body}>
                Previous brand collaborations and case studies
              </Text>
            </ListItem>
            <ListItem display="flex" alignItems="flex-start">
              <ListIcon as={() => <Box w="6px" h="6px" borderRadius="full" bg={ff.ink} mt={2} mr={3} />} />
              <Text fontSize="14px" color={ff.body}>
                Your content style and niche expertise
              </Text>
            </ListItem>
            <ListItem display="flex" alignItems="flex-start">
              <ListIcon as={() => <Box w="6px" h="6px" borderRadius="full" bg={ff.ink} mt={2} mr={3} />} />
              <Text fontSize="14px" color={ff.body}>
                Contact information and rates
              </Text>
            </ListItem>
          </List>
        </Box>
      </VStack>
    </Box>
  );
};

export default MediaKitEdit;